/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * useLinkedinStatus.js
 *
 * Shared LinkedIn connection probe. Every "write on LinkedIn" surface (the button next to an e-mail
 * action, the inbox badge, lead search) needs to know whether the signed-in user connected their own
 * LinkedIn account, so the answer is memoised at MODULE scope with a short TTL — mounting the hook on
 * a list of rows issues one request, not one per row. `ready` is false while the probe is in flight
 * and after any failure, so a gated button defaults to HIDDEN and never flashes in and out.
 *
 * Realtime: a `linkedin:message` socket event (see {@link module:services/linkedinApi.emitLinkedinEvent})
 * bumps `unreadCount` live; `refresh()` forces a re-probe (used after connect / disconnect).
 *
 * @module hooks/useLinkedinStatus
 */

import React from 'react';
import linkedinApi, {subscribeLinkedinEvents} from '../services/linkedinApi';
import {readLinkedinState} from '../helpers/linkedinUtils';

export {readLinkedinState};

/** How long a probe result is trusted before it is re-fetched. @type {number} */
const CACHE_TTL_MS = 2 * 60 * 1000;

/** A failed probe expires quickly so the next mount retries. @type {number} */
const FAILURE_RETRY_MS = 15000;

const HIDDEN_STATE = readLinkedinState({});

/** @type {{at: number, value: object}|null} */
let cache = null;

/** @type {Promise<object>|null} */
let inFlight = null;

/**
 * Fetch (and cache) the LinkedIn connection state. Never rejects.
 * @param {boolean} [force=false] Bypass the cache and any in-flight probe.
 * @returns {Promise<ReturnType<typeof readLinkedinState>>}
 */
export async function fetchLinkedinStatus(force = false) {
    if (!force && cache && Date.now() - cache.at < CACHE_TTL_MS) {
        return cache.value;
    }

    if (!force && inFlight) {
        return inFlight;
    }

    inFlight = (async () => {
        try {
            const value = readLinkedinState(await linkedinApi.getStatus());

            cache = {at: Date.now(), value};

            return value;
        } catch (_e) {
            cache = {at: Date.now() - (CACHE_TTL_MS - FAILURE_RETRY_MS), value: HIDDEN_STATE};

            return HIDDEN_STATE;
        } finally {
            inFlight = null;
        }
    })();

    return inFlight;
}

/** Drop the cached status (call after connect / disconnect). @returns {void} */
export function invalidateLinkedinStatus() {
    cache = null;
}

/**
 * @returns {ReturnType<typeof readLinkedinState> & {loading: boolean, refresh: () => Promise<void>}}
 */
export default function useLinkedinStatus() {
    const [state, setState] = React.useState(cache ? cache.value : HIDDEN_STATE);
    const [loading, setLoading] = React.useState(!cache);

    const refresh = React.useCallback(async () => {
        setLoading(true);
        const next = await fetchLinkedinStatus(true);

        setState(next);
        setLoading(false);
    }, []);

    React.useEffect(() => {
        let alive = true;

        fetchLinkedinStatus().then((next) => {
            if (alive) {
                setState(next);
                setLoading(false);
            }
        });

        const off = subscribeLinkedinEvents((event) => {
            if (!alive) {
                return;
            }

            if (event.type === 'message' && event.payload?.direction !== 'out') {
                setState((prev) => {
                    const reported = Number(event.payload?.unreadCount);
                    const next = {...prev, unreadCount: Number.isFinite(reported) ? reported : prev.unreadCount + 1};

                    cache = {at: cache ? cache.at : Date.now(), value: next};

                    return next;
                });
            } else if (event.type === 'status') {
                fetchLinkedinStatus(true).then((next) => alive && setState(next));
            }
        });

        return () => {
            alive = false;
            off();
        };
    }, []);

    return {...state, loading, refresh};
}
